import { around, Board } from '@brigh-am/black-hole-board/game';

// An offline, rule-based opponent operating on the Empty-Cell Numbers Game.
// Ported from black-hole-go/cmd/strat_curve/rulebot.go.
//
// Because all 20 played pieces sum to zero (+55 - 55 = 0), the game value is
// completely determined by the final black hole and its adjacent pieces.
// The state reduces to:
//   - E: bitmask of empty cells
//   - S[e]: P1's margin if empty cell e is the black hole right now (-sum(adjacent))
//   - O[e]: count of open (empty) neighbors of cell e
//
// The bot executes three distinct rules:
//   1. Opening Corners (ply <= 3 while an empty corner exists):
//      Take an empty corner (degree 2). When >= 2 corners are empty at ply >= 1,
//      break ties by minimizing the mover-signed sum of pieces within graph
//      distance 3 (Red/P1) or 2 (Blue/P2): sum_{d(c, x) <= D} sign * Cell[x].
//   2. Midgame Tactical StepCost Lookahead (n > tail empty cells):
//      Shallow minimax lookahead (1 reply after Red, 2 replies after Blue)
//      minimizing each player's StepCost:
//        StepCost(c) = 4*U(c) + (alpha*k + 2)*O[c] + deg[c] + sum_{e in N_E(c)} U(e)
//      where U(e) = sign * S[e] is the mover's hole utility.
//   3. Exact Numbers-Game Endgame (n <= tail empty cells, default tail = 11):
//      Exact minimax on the remaining empty-cell arithmetic game using closed-form
//      2..6 cell arithmetic and alpha-beta pruning.

export type Cell = [number, number];

export interface RuleBotOptions {
    cornerMaxPly?: number; // default: 3
    depth1?: number;       // default: 1
    depth2?: number;       // default: 2
    w1?: number;           // default: 3
    w2?: number;           // default: 2
    a1?: number;           // default: 3
    a2?: number;           // default: 4
    wo?: number;           // default: 2
    wn?: number;           // default: 1
    wd?: number;           // default: 1
    tail?: number;         // default: 11
}

const DEFAULT_OPTIONS: Required<RuleBotOptions> = {
    cornerMaxPly: 3,
    depth1: 1,
    depth2: 2,
    w1: 3,
    w2: 2,
    a1: 3,
    a2: 4,
    wo: 2,
    wn: 1,
    wd: 1,
    tail: 11,
};

const CELLS = 21;
const Inf = 127;

// Precomputed 6-row board geometry
const CELL_COORDS: [number, number][] = [];
const NBR: number[][] = [];
const NB_MASK = new Uint32Array(CELLS);
const DEG = new Int8Array(CELLS);
const DIST: Int8Array[] = Array.from({ length: CELLS }, () => new Int8Array(CELLS).fill(99));

for (let r = 0, idx = 0; r < 6; r++) {
    for (let c = 0; c <= r; c++, idx++) {
        CELL_COORDS.push([r, c]);
        const neighbors = [...around(r, c, 6)].map(nb => (nb.row * (nb.row + 1)) / 2 + nb.col);
        NBR.push(neighbors);
        DEG[idx] = neighbors.length;
        let m = 0;
        for (const n of neighbors) m |= 1 << n;
        NB_MASK[idx] = m;
    }
}

for (let i = 0; i < CELLS; i++) {
    DIST[i][i] = 0;
    const q = [i];
    while (q.length > 0) {
        const cur = q.shift()!;
        for (const nb of NBR[cur]) {
            if (DIST[i][nb] > DIST[i][cur] + 1) {
                DIST[i][nb] = (DIST[i][cur] + 1) as any;
                q.push(nb);
            }
        }
    }
}

function pairVal(c0: number, s0: number, c1: number, s1: number): number {
    let m = s0 < s1 ? s0 : s1;
    if ((NB_MASK[c0] & (1 << c1)) !== 0) m += 10;
    return m;
}

function triVal(c0: number, s0: number, c1: number, s1: number, c2: number, s2: number): number {
    const a01 = ((NB_MASK[c0] >> c1) & 1) * 10;
    const a02 = ((NB_MASK[c0] >> c2) & 1) * 10;
    const a12 = ((NB_MASK[c1] >> c2) & 1) * 10;
    let best = pairVal(c1, s1 - a01, c2, s2 - a02);
    const v1 = pairVal(c0, s0 - a01, c2, s2 - a12);
    if (v1 > best) best = v1;
    const v2 = pairVal(c0, s0 - a02, c1, s1 - a12);
    if (v2 > best) best = v2;
    return best;
}

function quadVal(cs: number[], ss: number[], alpha: number): number {
    let best = Inf;
    for (let i = 0; i < 4; i++) {
        const rc = [0, 0, 0];
        const rs = [0, 0, 0];
        let k = 0;
        const mask = NB_MASK[cs[i]];
        for (let j = 0; j < 4; j++) {
            if (j === i) continue;
            rc[k] = cs[j];
            rs[k] = ss[j] + (((mask >> cs[j]) & 1) * 9);
            k++;
        }
        const v = triVal(rc[0], rs[0], rc[1], rs[1], rc[2], rs[2]);
        if (v < best) {
            best = v;
            if (best <= alpha) return best;
        }
    }
    return best;
}

function quintVal(cs: number[], ss: number[], alpha: number, beta: number): number {
    let best = -Inf;
    for (let i = 0; i < 5; i++) {
        const rc = [0, 0, 0, 0];
        const rs = [0, 0, 0, 0];
        let k = 0;
        const mask = NB_MASK[cs[i]];
        for (let j = 0; j < 5; j++) {
            if (j === i) continue;
            rc[k] = cs[j];
            rs[k] = ss[j] - (((mask >> cs[j]) & 1) * 9);
            k++;
        }
        const v = quadVal(rc, rs, alpha);
        if (v > best) {
            best = v;
            if (v > alpha) {
                alpha = v;
                if (alpha >= beta) return best;
            }
        }
    }
    return best;
}

function sextVal(cs: number[], ss: number[], alpha: number, beta: number): number {
    let best = Inf;
    for (let i = 0; i < 6; i++) {
        const rc = [0, 0, 0, 0, 0];
        const rs = [0, 0, 0, 0, 0];
        let k = 0;
        const mask = NB_MASK[cs[i]];
        for (let j = 0; j < 6; j++) {
            if (j === i) continue;
            rc[k] = cs[j];
            rs[k] = ss[j] + (((mask >> cs[j]) & 1) * 8);
            k++;
        }
        const v = quintVal(rc, rs, alpha, beta);
        if (v < best) {
            best = v;
            if (v < beta) {
                beta = v;
                if (alpha >= beta) return best;
            }
        }
    }
    return best;
}

class NumState {
    ply: number;
    empty: number;
    n: number;
    pot: number;
    s: Int16Array;
    o: Int8Array;

    constructor(
        ply: number,
        empty: number,
        n: number,
        pot: number,
        s: Int16Array,
        o: Int8Array,
    ) {
        this.ply = ply;
        this.empty = empty;
        this.n = n;
        this.pot = pot;
        this.s = s;
        this.o = o;
    }

    static fromBoard(board: Board): NumState {
        let empty = 0;
        const ply = board.filled;
        const s = new Int16Array(CELLS);
        const o = new Int8Array(CELLS);
        for (let i = 0; i < CELLS; i++) {
            const [r, c] = CELL_COORDS[i];
            if (board.at(r, c) === 0) {
                empty |= 1 << i;
            }
        }
        for (let i = 0; i < CELLS; i++) {
            if ((empty & (1 << i)) !== 0) {
                let sum = 0;
                let open = 0;
                for (const nb of NBR[i]) {
                    const [nr, nc] = CELL_COORDS[nb];
                    const val = board.at(nr, nc);
                    sum -= val;
                    if (val === 0) open++;
                }
                s[i] = sum;
                o[i] = open;
            }
        }
        return new NumState(ply, empty, CELLS - ply, 0, s, o);
    }

    play(c: number, a1: number, a2: number, wo: number, wn: number, wd: number): NumState {
        const k = Math.floor(this.ply / 2) + 1;
        const isRed = (this.ply & 1) === 0;
        const sig = isRed ? 1 : -1;
        const alpha = isRed ? a1 : a2;
        const v = sig * k;
        const oc = this.o[c];
        const newEmpty = this.empty ^ (1 << c);
        const ns = new Int16Array(this.s);
        const no = new Int8Array(this.o);
        let newPot = this.pot - 4 * this.s[c] - sig * (alpha * k + wo) * oc - sig * wd * DEG[c];
        const mask = NB_MASK[c] & newEmpty;
        let nbrSum = 0;
        for (let m = mask; m !== 0; m &= m - 1) {
            const e = Math.clz32(m & -m) ^ 31;
            nbrSum += this.s[e];
            ns[e] -= v;
            no[e]--;
        }
        newPot -= sig * wn * nbrSum;
        return new NumState(this.ply + 1, newEmpty, this.n - 1, newPot, ns, no);
    }

    stepCost(c: number, conf: Required<RuleBotOptions>): number {
        const k = Math.floor(this.ply / 2) + 1;
        const isRed = (this.ply & 1) === 0;
        const sig = isRed ? 1 : -1;
        const alpha = isRed ? conf.a1 : conf.a2;
        const oc = this.o[c];
        const newEmpty = this.empty ^ (1 << c);
        const mask = NB_MASK[c] & newEmpty;
        let nbrSum = 0;
        for (let m = mask; m !== 0; m &= m - 1) {
            const e = Math.clz32(m & -m) ^ 31;
            nbrSum += sig * this.s[e];
        }
        const u = sig * this.s[c];
        return 4 * u + (alpha * k + conf.wo) * oc + conf.wd * DEG[c] + conf.wn * nbrSum;
    }

    baseCost(c: number, w1: number, w2: number): number {
        const isRed = (this.ply & 1) === 0;
        const sig = isRed ? 1 : -1;
        const w = isRed ? w1 : w2;
        const piece = Math.floor(this.ply / 2) + 1;
        const u = sig * this.s[c];
        const oc = this.o[c];
        const primary = 4 * u + w * piece * oc;
        return primary * 1024 + (u + 128) * 8 + oc;
    }

    orderedMoves(w1: number, w2: number): number[] {
        const ms: { c: number; cost: number }[] = [];
        for (let m = this.empty; m !== 0; m &= m - 1) {
            const c = Math.clz32(m & -m) ^ 31;
            ms.push({ c, cost: this.baseCost(c, w1, w2) });
        }
        ms.sort((a, b) => a.cost - b.cost);
        return ms.map(x => x.c);
    }

    solveExact(alpha: number, beta: number): number {
        switch (this.n) {
            case 1: {
                const c0 = Math.clz32(this.empty & -this.empty) ^ 31;
                return this.s[c0];
            }
            case 2: {
                let m = this.empty;
                const c0 = Math.clz32(m & -m) ^ 31;
                m &= m - 1;
                const c1 = Math.clz32(m & -m) ^ 31;
                return pairVal(c0, this.s[c0], c1, this.s[c1]);
            }
            case 3: {
                let m = this.empty;
                const c0 = Math.clz32(m & -m) ^ 31;
                m &= m - 1;
                const c1 = Math.clz32(m & -m) ^ 31;
                m &= m - 1;
                const c2 = Math.clz32(m & -m) ^ 31;
                return triVal(c0, this.s[c0], c1, this.s[c1], c2, this.s[c2]);
            }
            case 4: {
                let m = this.empty;
                const cs = [0, 0, 0, 0];
                const ss = [0, 0, 0, 0];
                for (let i = 0; i < 4; i++) {
                    const c = Math.clz32(m & -m) ^ 31;
                    m &= m - 1;
                    cs[i] = c;
                    ss[i] = this.s[c];
                }
                return quadVal(cs, ss, alpha);
            }
            case 5: {
                let m = this.empty;
                const cs = [0, 0, 0, 0, 0];
                const ss = [0, 0, 0, 0, 0];
                for (let i = 0; i < 5; i++) {
                    const c = Math.clz32(m & -m) ^ 31;
                    m &= m - 1;
                    cs[i] = c;
                    ss[i] = this.s[c];
                }
                return quintVal(cs, ss, alpha, beta);
            }
            case 6: {
                let m = this.empty;
                const cs = [0, 0, 0, 0, 0, 0];
                const ss = [0, 0, 0, 0, 0, 0];
                for (let i = 0; i < 6; i++) {
                    const c = Math.clz32(m & -m) ^ 31;
                    m &= m - 1;
                    cs[i] = c;
                    ss[i] = this.s[c];
                }
                return sextVal(cs, ss, alpha, beta);
            }
        }

        const moves = this.orderedMoves(3, 2);
        if ((this.ply & 1) === 0) {
            let best = -Inf;
            for (const c of moves) {
                const v = this.play(c, 3, 4, 2, 1, 1).solveExact(alpha, beta);
                if (v > best) {
                    best = v;
                    if (v > alpha) {
                        alpha = v;
                        if (alpha >= beta) break;
                    }
                }
            }
            return best;
        } else {
            let best = Inf;
            for (const c of moves) {
                const v = this.play(c, 3, 4, 2, 1, 1).solveExact(alpha, beta);
                if (v < best) {
                    best = v;
                    if (v < beta) {
                        beta = v;
                        if (alpha >= beta) break;
                    }
                }
            }
            return best;
        }
    }

    searchTactical(
        depth: number,
        alpha: number,
        beta: number,
        w1: number,
        w2: number,
        a1: number,
        a2: number,
        wo: number,
        wn: number,
        wd: number,
    ): number {
        if (this.n <= 5) {
            return this.solveExact(-Inf, Inf) * 100;
        }
        if (depth <= 0) {
            return this.pot;
        }
        const moves = this.orderedMoves(w1, w2);
        if ((this.ply & 1) === 0) {
            let best = -1 << 29;
            for (const c of moves) {
                const v = this.play(c, a1, a2, wo, wn, wd).searchTactical(
                    depth - 1,
                    alpha,
                    beta,
                    w1,
                    w2,
                    a1,
                    a2,
                    wo,
                    wn,
                    wd,
                );
                if (v > best) {
                    best = v;
                    if (v > alpha) {
                        alpha = v;
                        if (alpha >= beta) break;
                    }
                }
            }
            return best;
        } else {
            let best = 1 << 29;
            for (const c of moves) {
                const v = this.play(c, a1, a2, wo, wn, wd).searchTactical(
                    depth - 1,
                    alpha,
                    beta,
                    w1,
                    w2,
                    a1,
                    a2,
                    wo,
                    wn,
                    wd,
                );
                if (v < best) {
                    best = v;
                    if (v < beta) {
                        beta = v;
                        if (alpha >= beta) break;
                    }
                }
            }
            return best;
        }
    }
}

function cornerCandidates(board: Board, emptyMask: number): number[] {
    const corners = [0, 15, 20].filter(c => (emptyMask & (1 << c)) !== 0);
    if (corners.length <= 1 || board.filled === 0) {
        return corners;
    }
    const sign = Math.sign(board.nextMove());
    const maxDist = sign < 0 ? 2 : 3;
    let best = 1 << 30;
    let out: number[] = [];
    for (const c of corners) {
        let score = 0;
        for (let i = 0; i < CELLS; i++) {
            if (DIST[c][i] <= maxDist) {
                const [r, col] = CELL_COORDS[i];
                score += sign * board.at(r, col);
            }
        }
        if (score < best) {
            best = score;
            out = [];
        }
        if (score === best) {
            out.push(c);
        }
    }
    return out;
}

/** Returns the candidate moves considered equally optimal by the RuleBot. */
export function phaseBotCandidates(board: Board, opts: RuleBotOptions = {}): Cell[] {
    // If not a standard 6-row board, fall back to basic legal empty cells
    if (board.rows !== 6) {
        const out: Cell[] = [];
        for (let r = 0; r < board.rows; r++) {
            for (let c = 0; c <= r; c++) {
                if (board.at(r, c) === 0) out.push([r, c]);
            }
        }
        return out;
    }

    const conf: Required<RuleBotOptions> = { ...DEFAULT_OPTIONS, ...opts };
    const st = NumState.fromBoard(board);
    const sign = Math.sign(board.nextMove());

    // 1. Exact numbers-game endgame when <= tail empty cells remain.
    if (st.n <= conf.tail) {
        const moves = st.orderedMoves(conf.w1, conf.w2);
        let out: number[] = [];
        if (sign > 0) {
            let best = -Inf;
            for (const c of moves) {
                const v = st.play(c, conf.a1, conf.a2, conf.wo, conf.wn, conf.wd).solveExact(best - 1, Inf);
                if (v > best) {
                    best = v;
                    out = [];
                }
                if (v === best) {
                    out.push(c);
                }
            }
        } else {
            let best = Inf;
            for (const c of moves) {
                const v = st.play(c, conf.a1, conf.a2, conf.wo, conf.wn, conf.wd).solveExact(-Inf, best + 1);
                if (v < best) {
                    best = v;
                    out = [];
                }
                if (v === best) {
                    out.push(c);
                }
            }
        }
        return out.map(c => CELL_COORDS[c]);
    }

    // 2. Opening corner rule (ply <= cornerMaxPly while an empty corner exists).
    const cornerMask = (1 << 0) | (1 << 15) | (1 << 20);
    if (board.filled <= conf.cornerMaxPly && (st.empty & cornerMask) !== 0) {
        return cornerCandidates(board, st.empty).map(c => CELL_COORDS[c]);
    }

    // 3. Midgame numbers-game tactical evaluation.
    const depth = sign < 0 ? conf.depth2 : conf.depth1;
    let bestV = -1 << 30;
    let bestBase = 1 << 30;
    let out: number[] = [];

    for (let m = st.empty; m !== 0; m &= m - 1) {
        const c = Math.clz32(m & -m) ^ 31;
        const v =
            sign *
            st
                .play(c, conf.a1, conf.a2, conf.wo, conf.wn, conf.wd)
                .searchTactical(
                    depth,
                    -1 << 29,
                    1 << 29,
                    conf.w1,
                    conf.w2,
                    conf.a1,
                    conf.a2,
                    conf.wo,
                    conf.wn,
                    conf.wd,
                );
        const base = st.baseCost(c, conf.w1, conf.w2);
        if (v > bestV || (v === bestV && base < bestBase)) {
            bestV = v;
            bestBase = base;
            out = [];
        }
        if (v === bestV && base === bestBase) {
            out.push(c);
        }
    }

    return out.map(c => CELL_COORDS[c]);
}

/** Picks a move from candidates, breaking ties using random generator. */
export function phaseBotMove(
    board: Board,
    random: () => number = Math.random,
    opts?: RuleBotOptions,
): Cell | null {
    if (board.isGameOver()) return null;
    const candidates = phaseBotCandidates(board, opts);
    if (candidates.length === 0) return null;
    return candidates[Math.floor(random() * candidates.length)];
}

export interface StepCostInfo {
    cost: number;
    isBest: boolean;
}

/**
 * Calculates the RuleBot StepCost for every empty cell on the board.
 * Lower cost indicates a better move (penalizes danger, exposure, and poisoning).
 */
export function getAllStepCosts(
    board: Board,
    opts: RuleBotOptions = {},
): Map<string, StepCostInfo> {
    const map = new Map<string, StepCostInfo>();
    if (board.rows !== 6 || board.isGameOver()) return map;

    const conf: Required<RuleBotOptions> = { ...DEFAULT_OPTIONS, ...opts };
    const st = NumState.fromBoard(board);

    let minCost = Infinity;
    const entries: { key: string; cost: number }[] = [];

    for (let m = st.empty; m !== 0; m &= m - 1) {
        const c = Math.clz32(m & -m) ^ 31;
        const [r, col] = CELL_COORDS[c];
        const cost = st.stepCost(c, conf);
        if (cost < minCost) minCost = cost;
        entries.push({ key: `${r}-${col}`, cost });
    }

    for (const { key, cost } of entries) {
        map.set(key, { cost, isBest: cost === minCost });
    }

    return map;
}

